import {QueryList} from '@angular/core';
import {MultiSelect} from 'primeng/multiselect';
import {ApplicationErreur} from '../exception/ApplicationErreur';
import { HttpHeaders } from '@angular/common/http';

/**
 * Fonction permettant d'arrondir un nombre à la décimale demandée.
 *
 * ATTENTION:
 * - Cette fonction vérifie que la valeur passée en paramètre est bien un nombre ou un nombre dans une chaine de caractères et que c'est un nombre fini.
 * - Dans le cas ou vous ne précisez pas le nombre de décimales, le filtre prend 2 décimales.
 * - Dans le cas ou l'attribut 'decimal' est faux (ex: 'a'), on prend 2 pour qu'il y ait un affichage.
 * @param value la valeur à arrondir.
 * @param nbDecimals le nombre de décimales qu'on veut conserver.
 * @returns {number|undefined} le number arrondi, ou undefined si value n'est pas une valeur numérique.
 */
export function nombreArrondi(value: number, nbDecimals: number): number {
	// Test si 'value' est bien un nombre ou un nombre dans une chaine de caractère.
	if ((!isNaN(value) && isNumber(value)) || isNumber(value)) {
		// Test si 'decimal' est bien un nombre ou un nombre dans une chaine de caractère et si il est supérieur à 0

		if (!isNumber(nbDecimals) || nbDecimals < 0) {
			// Si le paramètre n'est pas renseigné correctement, par défaut on met 2 chiffres après la virgule.
			nbDecimals = 2;
		}
		const fixedNumber = parseFloat(Number(value).toFixed(nbDecimals));
		if (fixedNumber === 0) {
			return 0;
		}
		const multiplicateur = 'e+' + nbDecimals;
		const diviseur = 'e-' + nbDecimals;
		return +(parseFloat(Math.round(parseFloat(value.toString() + multiplicateur)).toString() + diviseur));
	}
	return undefined;
}

export function isNumber(valeur: any) {
	return typeof valeur === 'number';
}

export function isNullOrUndefined(valeur: any) {
	return valeur === null || valeur === undefined;
}

export function isNotNullOrUndefined(valeur: any) {
	return valeur !== null && valeur !== undefined;
}

export function isBlank(valeur: string) {
	return valeur === null || valeur === undefined || valeur.length === 0;
}

/**
 * Permet de décoder et parser le message d'erreur qui est en JSON.
 * Cela retourne un objet ApplicationErreur.
 *
 * @param messageErreur Le message renvoyé par le back
 */
export function decoderEtParserReponseEnErreur(messageErreur: any): ApplicationErreur {
	const dataView = new DataView(messageErreur.error);
	const decoder = new TextDecoder('utf8');
	return JSON.parse(decoder.decode(dataView));
}

/**
 * Permet de garder les valeurs des filtres multiselect
 *
 * @param nomDuFiltre Le 'name' du filtre
 * @param valeursPossibles Les valeurs à ajouter au filtre
 */
export function concatenerPrecedentFiltresSelectionnes(multiSelectFilters: QueryList<MultiSelect>, nomDuFiltre: any, valeursPossibles: any[]): any[] {
	let nouvellesValeurs = valeursPossibles;
	if (multiSelectFilters && multiSelectFilters['_results']) {
		multiSelectFilters['_results'].forEach(filter => {
			if (filter.name === nomDuFiltre && filter.value !== undefined) {
				nouvellesValeurs = nouvellesValeurs.concat(filter.value);
			}
		});
	}
	return nouvellesValeurs;
}

/**
 * Permet de savoir si la requête est dans le cache ou non.
 * Retourne True si la requête n'est PAS dans le cache. False sinon.
 *
 * @param httpHeaders Les headers de la réponse de la requête HTTP.
 */
export function estRequeteHorsCache(httpHeaders: HttpHeaders) : boolean {
	const cacheControl = httpHeaders?.get('Cache-Control');
	return isBlank(cacheControl) || cacheControl?.includes('no-cache') || cacheControl?.includes('no-store') || cacheControl?.includes('max-age=0');
}
